<template>
  <div class="container">
    <ol class="breadcrumb">
      <li><router-link :to="{ name: 'dashboard' }">Dashboard</router-link></li>
      <li><router-link :to="{name: 'serial-list'}">Serials</router-link></li>
      <li class="active">{{ serial }}</li>
    </ol>
    <!-- {{ sortedLicenses }} -->
    <div class="row">
      <div class="col-sm-6">
        <date-range-picker @daterange-change="dateRangeChange"></date-range-picker>
      </div>
      <div class="col-sm-6">
        <h5>{{ dateRangeFormat(dateStart) }} - {{ dateRangeFormat(dateEnd) }}</h5>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-6">
        <track-days :date-start="epochStart" :date-end="epochEnd" :days="sortedTrackLogs"></track-days>
      </div>
    </div>

    <div class="row">
      <ul class="nav nav-tabs">
        <li v-bind:class="activeTab === 'details' ? 'active' : ''"><a href="#" @click.stop.prevent="showTab('details')">Details</a></li>
        <li v-bind:class="activeTab === 'licenses' ? 'active' : ''"><a href="#" @click.stop.prevent="showTab('licenses')">Licenses</a></li>
        <li v-bind:class="activeTab === 'proxy' ? 'active' : ''"><a href="#" @click.stop.prevent="showTab('proxy')">Proxy Stats</a></li>
        <li v-bind:class="activeTab === 'os' ? 'active' : ''"><a href="#" @click.stop.prevent="showTab('os')">OS Stats</a></li>
      </ul>
    </div>

    <div v-if="activeTab === 'details'">
      <div class="row">
        <div class="col-md-6 col-sm-12">
          <table class="table">
            <thead>
            </thead>
            <tbody>
              <tr>
                <td>Serial</td>
                <td>{{ serial }}</td>
              </tr>
              <tr>
                <td>Customer</td>
                <td>{{ serialDetails.customer }}</td>
              </tr>
              <tr>
                <td>Country</td>
                <td>{{ locationCountry }}</td>
              </tr>
              <tr>
                <td>City</td>
                <td>{{ locationCity }}</td>
              </tr>
              <tr>
                <td>Wedge SO Version</td>
                <td>{{ softwareVersion }}</td>
              </tr>
              <tr>
                <td>Service Tag</td>
                <td>{{ serialDetails.serviceTag }}</td>
              </tr>
              <tr>
                <td>MAC Address</td>
                <td>{{ serialDetails.mac }}</td>
              </tr>
              <tr>
                <td>Support Group</td>
                <td>{{ serialDetails.supportGroup }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="col-md-6 col-sm-12">
          <google-map :coordinates="locationCoordinates" height="250px"></google-map>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'licenses'">
      <div class="row">
        <div v-if="serialLicenses.length === 0" class="col-sm-12">
          <p>No licenses have been activated for this appliance serial.</p>
        </div>

        <div v-else class="col-sm-12">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Activated</th>
                  <th>Issued</th>
                  <th>Expiry Date</th>
                  <th>Expires</th>
                  <th>Duration</th>
                  <th v-for="service in licenseServices">{{ service }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="license in reducedLicenses" v-bind:class="licenseRowClass(license.expired, license.expiresInDays)">
                  <td>{{ license.key }}</td>
                  <td>{{ license.activated }}</td>
                  <td>{{ license.issued }}</td>
                  <td>{{ license.expiryDate }}</td>
                  <td>{{ license.expiresFromNow }}</td>
                  <td>{{ license.duration }}</td>
                  <td v-for="service in licenseServices">
                    <span v-if="license.services[service]" class="glyphicon glyphicon-ok" aria-hidden="true"></span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>


    <div v-if="activeTab === 'proxy'">
      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="requestsChartOptions" :series="requestsChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
          <high-chart :options="connectionsChartOptions" :series="connectionsChartSeries"></high-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="malwareChartOptions" :series="malwareChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
          <high-chart :options="bytesChartOptions" :series="bytesChartSeries"></high-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="aiMalwareChartOptions" :series="aiMalwareChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
          <high-chart :options="aiGraywareChartOptions" :series="aiGraywareChartSeries"></high-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="sandboxMalwareChartOptions" :series="sandboxMalwareChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
          <high-chart :options="sandboxCleanChartOptions" :series="sandboxCleanChartSeries"></high-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="urlsChartOptions" :series="urlsChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
          <high-chart :options="spamChartOptions" :series="spamChartSeries"></high-chart>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'os'">
      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="netIngressChartOptions" :series="netIngressChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
          <high-chart :options="netEgressChartOptions" :series="netEgressChartSeries"></high-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="netBridgeChartOptions" :series="netBridgeChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
          <high-chart :options="netControlChartOptions" :series="netControlChartSeries"></high-chart>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-6 col-md-6">
          <high-chart :options="uespFsCfChartOptions" :series="uespFsCfChartSeries"></high-chart>
        </div>
        <div class="col-sm-6 col-md-6">
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>

<script>
import _ from 'underscore'
import moment from 'moment'
import chartLib from '../lib/chart'
import licenseLib from '../lib/license'
import HighChart from './HighChart'
import DateRangePicker from './DateRangePicker'
import TrackDays from './TrackDays'
import EventDays from './EventDays'
import GoogleMap from './GoogleMap'
import * as mutationTypes from '../store/mutation-types'
import C from '../constants'
import statsLib from '../lib/stats'

export default {
  components: {
    HighChart,
    DateRangePicker,
    TrackDays,
    EventDays,
    GoogleMap
  },

  data () {
    return {
      serial: this.$route.params.id,
      serialDetails: {},
      serialLicenses: [],

      trackLogs: [],
      locations: [],

      daterange: '',

      // tabs
      activeTab: 'details',
      tabs: [ 'details', 'licenses', 'proxy', 'os' ],

      // chart series and options
      malwareChartSeries: [],
      malwareChartOptions: { chart: {} },

      requestsChartSeries: [],
      requestsChartOptions: { chart: {} },

      bytesChartSeries: [],
      bytesChartOptions: { chart: {} },

      connectionsChartSeries: [],
      connectionsChartOptions: { chart: {} },

      aiMalwareChartSeries: [],
      aiMalwareChartOptions: { chart: {} },

      aiGraywareChartSeries: [],
      aiGraywareChartOptions: { chart: {} },

      sandboxMalwareChartSeries: [],
      sandboxMalwareChartOptions: { chart: {} },

      sandboxCleanChartSeries: [],
      sandboxCleanChartOptions: { chart: {} },

      urlsChartSeries: [],
      urlsChartOptions: { chart: {} },

      spamChartSeries: [],
      spamChartOptions: { chart: {} },

      uespFsCfChartSeries: [],
      uespFsCfChartOptions: { chart: {} },

      netIngressChartSeries: [],
      netIngressChartOptions: { chart: {} },

      netEgressChartSeries: [],
      netEgressChartOptions: { chart: {} },

      netControlChartSeries: [],
      netControlChartOptions: { chart: {} },

      netBridgeChartSeries: [],
      netBridgeChartOptions: { chart: {} }
    }
  },

  mounted () {
    this.malwareChartOptions = chartLib.chartOptions('column', 'AV Malware', 'count')
    this.bytesChartOptions = chartLib.chartOptions('column', 'Bytes', 'count')
    this.connectionsChartOptions = chartLib.chartOptions('column', 'Connections', 'count')
    this.requestsChartOptions = chartLib.chartOptions('column', 'Requests', 'count')
    this.aiMalwareChartOptions = chartLib.chartOptions('column', 'AI Malware', 'count')
    this.aiGraywareChartOptions = chartLib.chartOptions('column', 'AI Grayware', 'count')
    this.sandboxMalwareChartOptions = chartLib.chartOptions('column', 'Sandbox Malware', 'count')
    this.sandboxCleanChartOptions = chartLib.chartOptions('column', 'Sandbox Clean', 'count')
    this.urlsChartOptions = chartLib.chartOptions('column', 'Urls', 'count')
    this.spamChartOptions = chartLib.chartOptions('column', 'Spam', 'count')

    this.uespFsCfChartOptions = chartLib.percentageChartOptions('Disk Utilization', '')

    this.netIngressChartOptions = chartLib.chartOptions('line', 'Ingress', 'bytes/sec')
    this.netEgressChartOptions = chartLib.chartOptions('line', 'Egress', 'bytes/sec')
    this.netControlChartOptions = chartLib.chartOptions('line', 'Control', 'bytes/sec')
    this.netBridgeChartOptions = chartLib.chartOptions('line', 'Bridge', 'bytes/sec')

    this.fetch()
  },

  computed: {
    dateRange: {
      get () {
        return this.$store.state.filterDateRange.dateRange
      },

      set (value) {
        this.$store.commit(mutationTypes.FILTER_DATE_RANGE, value)
      }
    },
    dateStart () {
      return moment(this.dateRange[0], C.M_FMT_MS).utc()
    },

    dateEnd () {
      return moment(this.dateRange[1], C.M_FMT_MS).utc()
    },
    epochStart () {
      return this.dateRange[0]
    },
    epochEnd () {
      return this.dateRange[1]
    },
    sortedTrackLogs () {
      return _.sortBy(this.trackLogs, 'range')
    },
    sortedLicenses () {
      return _.sortBy(this.serialLicenses, (item) => {
        return item.activation.activateTime
      }).reverse()
    },
    licenseServices () {
      var services = []
      this.serialLicenses.forEach((license) => {
        services = services.concat(_.keys(license.attributes.license.service))
      })
      return _.sortBy(_.uniq(services))
    },
    reducedLicenses () {
      const FMT_LIC = 'YYYY-MM-DD HH:mm'
      return _.map(this.sortedLicenses, (lic) => {
        var services = {}
        var expired = false
        var expireSet = 'expires' in lic.attributes.license.root
        var durationSet = 'duration' in lic.attributes.license.root
        var duration = durationSet ? lic.attributes.license.root.duration : null
        var expiryDate = licenseLib.licenseExpiryDate(lic.activation.activateTime, lic.attributes.license.root.expires, duration)

        // license services
        _.each(this.licenseServices, (service) => {
          services[service] = service in lic.attributes.license.service && lic.attributes.license.service[service].enabled === 'true'
        })

        // expired
        expired = moment().isAfter(expiryDate)

        return {
          key: lic.license.productKey,
          activated: moment(lic.activation.activateTime).format(FMT_LIC),
          issued: moment(lic.attributes.license.root.issued).format(FMT_LIC),
          expires: expireSet ? moment(lic.attributes.license.root.expires) : '',
          duration: duration || '',
          services: services,
          expired: expired,
          expiryDate: expiryDate.format(FMT_LIC),
          expiresFromNow: expiryDate.fromNow(),
          expiresInDays: expiryDate.diff(moment(), 'days')
        }
      })
    },
    softwareVersion () {
      var length = this.sortedTrackLogs.length
      return length > 0 ? this.sortedTrackLogs[length - 1].softwareVersion : ''
    },
    locationCoordinates () {
      return _.map(this.locations, (loc) => {
        return {
          lat: loc.latitude,
          long: loc.longitude
        }
      })
    },
    locationCountry () {
      return this.locationJoinField('country_name')
    },
    locationCity () {
      return this.locationJoinField('city')
    }
  },

  methods: {
    dateRangeChange (e) {
      this.dateRange = e
    },
    dateRangeFormat (date) {
      return moment(date).format(C.M_FMT_READABLE_WEEKDAY)
    },

    licenseRowClass (expired, expiresInDays) {
      const WARN_DAYS = 10

      if (expired) {
        return 'danger'
      }
      return expiresInDays > 0 && expiresInDays < WARN_DAYS ? 'warning' : ''
    },
    showTab (tabName) {
      this.activeTab = tabName
    },
    locationJoinField (fieldName) {
      if (this.locations.length === 0) {
        return ''
      }
      return _.uniq(_.pluck(this.locations, fieldName)).join(', ')
    },
    formatDate: function (date, format) {
      var dateFormat = 'ddd MMM D, YYYY'
      return moment(date, format).format(dateFormat)
    },

    fetch () {
      this.fetchTrack()
      // console.log('selectSerial', this.serial, event.target.value)
      this.fetchStats()
      this.fetchDetails()
      this.fetchLicenses()
    },

    fetchTrack () {
      this.$apiWsoc.fetchTrack(this.serial, this.epochStart, this.epochEnd)
      .then((result) => {
        this.trackLogs = result.result.Items
        this.locations = result.locations
      })
      .catch((error) => {
        console.log('fetch track error', error)
      })
    },

    fetchDetails: function () {
      // console.log('fetching details for ', this.selected)
      this.$apiWsoc.fetchDetails(this.serial).then((result) => {
        // console.log('DETAILS', result)
        this.serialDetails = result.result
      })
      .catch((error) => {
        console.log('error', error)
      })
    },

    fetchLicenses: function () {
      this.$apiWsoc.fetchLicenses(this.serial).then((result) => {
        console.log('LICENSES', result)
        this.serialLicenses = result.result
      })
      .catch((error) => {
        console.log('error', error)
      })
    },

    fetchStats: function () {
      // console.log('fetching stats for', this.selected, this.dateRange[0], this.dateRange[1])

      this.$apiWsoc.fetchStats(this.serial, this.dateRange[0], this.dateRange[1]).then((result) => {
        var data = result.data
        var seriesData = function (dataObj) {
          return _.values(dataObj).map(Number)
        }

        var chartSeries = function (statName) {
          return statsLib.protocols.map((protocol) => {
            return {
              name: protocol,
              data: seriesData(data[protocol + '.' + statName])
            }
          })
        }
        // console.log('STATS', result)

        this.requestsChartSeries = chartSeries('totalRequests')
        this.requestsChartOptions.xAxis.categories = _.keys(data['HTTP.totalRequests'])

        this.malwareChartSeries = chartSeries('blockedVirus')
        this.malwareChartOptions.xAxis.categories = _.keys(data['HTTP.blockedVirus'])

        this.bytesChartSeries = chartSeries('totalBytesRead')
        this.bytesChartOptions.xAxis.categories = _.keys(data['HTTP.totalBytesRead'])

        this.connectionsChartSeries = chartSeries('totalConnections')
        this.connectionsChartOptions.xAxis.categories = _.keys(data['HTTP.totalConnections'])

        this.aiMalwareChartSeries = chartSeries('cylanceAvMalware')
        this.aiMalwareChartOptions.xAxis.categories = _.keys(data['HTTP.cylanceAvMalware'])

        this.aiGraywareChartSeries = chartSeries('cylanceAvGrayware')
        this.aiGraywareChartOptions.xAxis.categories = _.keys(data['HTTP.cylanceAvGrayware'])

        this.sandboxMalwareChartSeries = chartSeries('sandboxAvHashMalware')
        this.sandboxMalwareChartOptions.xAxis.categories = _.keys(data['HTTP.sandboxAvHashMalware'])

        this.sandboxCleanChartSeries = chartSeries('sandboxAvHashClean')
        this.sandboxCleanChartOptions.xAxis.categories = _.keys(data['HTTP.sandboxAvHashClean'])

        this.urlsChartSeries = [{
          name: 'Blocked URL',
          data: seriesData(data['HTTP.blockedUrls'])
        }, {
          name: 'Blocked WF URL',
          data: seriesData(data['HTTP.blockedWfUrls'])
        }, {
          name: 'Warned WF URL',
          data: seriesData(data['HTTP.warnedWfUrls'])
        }, {
          name: 'Blocked CM URL',
          data: seriesData(data['HTTP.blockedCMUrls'])
        }, {
          name: 'Warned CM URL',
          data: seriesData(data['HTTP.warnedCMUrls'])
        }]
        this.urlsChartOptions.xAxis.categories = _.keys(data['HTTP.blockedUrls'])

        this.spamChartSeries = [{
          name: 'SMTP',
          data: seriesData(data['SMTP.spamCount'])
        }, {
          name: 'POP3',
          data: seriesData(data['POP3.spamCount'])
        }, {
          name: 'IMAP',
          data: seriesData(data['IMAP.spamCount'])
        }]
        this.spamChartOptions.xAxis.categories = _.keys(data['SMTP.spamCount'])

        this.uespFsCfChartSeries = [{
          name: 'Free',
          data: seriesData(data['uesp-fs-cf']).map((val) => { return 100 - val })
        }, {
          name: 'Used',
          data: seriesData(data['uesp-fs-cf'])
        }]
        this.uespFsCfChartOptions.xAxis.categories = _.keys(data['uesp-fs-cf'])

        this.netIngressChartSeries = [{
          name: 'tx bytes',
          data: seriesData(data['ingress_tx_bytes'])
        }, {
          name: 'rx bytes',
          data: seriesData(data['ingress_rx_bytes'])
        }]
        this.netIngressChartOptions.xAxis.categories = _.keys(data['ingress_tx_bytes'])

        this.netEgressChartSeries = [{
          name: 'tx bytes',
          data: seriesData(data['egress_tx_bytes'])
        }, {
          name: 'rx bytes',
          data: seriesData(data['egress_rx_bytes'])
        }]
        this.netEgressChartOptions.xAxis.categories = _.keys(data['egress_tx_bytes'])

        this.netControlChartSeries = [{
          name: 'tx bytes',
          data: seriesData(data['control_tx_bytes'])
        }, {
          name: 'rx bytes',
          data: seriesData(data['control_rx_bytes'])
        }]
        this.netControlChartOptions.xAxis.categories = _.keys(data['control_tx_bytes'])

        this.netBridgeChartSeries = [{
          name: 'tx bytes',
          data: seriesData(data['bridge_tx_bytes'])
        }, {
          name: 'rx bytes',
          data: seriesData(data['bridge_rx_bytes'])
        }]
        this.netBridgeChartOptions.xAxis.categories = _.keys(data['bridge_tx_bytes'])
      }).catch((error) => {
        console.log('error', error)
      })
    }
  },

  watch: {
    dateRange: function () {
      this.fetchStats()
      this.fetchTrack()
    }
  }
}
</script>



// WEBPACK FOOTER //
// Serial.vue?c0711f38